import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileArchive, Download, Calendar, Clock, 
  Copy, ExternalLink, Trash2, RefreshCw, Search, X,
  User, Mail, Crown, Save, Image, Bell, BellOff, 
  HardDrive, BarChart3, Loader2, DollarSign, Tag,
  TrendingUp, ShoppingCart, Check, Link2, Unlink, AlertCircle, CheckCircle2,
  Puzzle, Lock, Eye, EyeOff, ShieldCheck, Shield, Upload, ArrowUp, ArrowDown, Video, Monitor, Plus, Shuffle, MousePointerClick
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import BackgroundClicksChart from '../components/BackgroundClicksChart';
import BackgroundPreviewSlideshow from '../components/BackgroundPreviewSlideshow';
import axios from 'axios';

const BgLinkInput = ({ bgId, initialLink, onSave }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && inputRef.current !== document.activeElement) {
      inputRef.current.value = initialLink || '';
    }
  }, [initialLink]);

  return (
    <input
      ref={inputRef}
      type="text"
      defaultValue={initialLink || ''}
      onBlur={(e) => {
        const val = e.target.value;
        if (val !== (initialLink || '')) onSave(bgId, 'link', val);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      placeholder="https://..."
      data-testid={`bg-link-input-${bgId}`}
      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:border-blue-400"
    />
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, token, isAuthenticated, isPro, isAdmin, loading, updateProfile, updateNotifications, downloadNotifications } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [activeTab, setActiveTab] = useState('files');
  
  // Profile states
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // 2FA states
  const [show2faSetup, setShow2faSetup] = useState(false);
  const [qrCode2fa, setQrCode2fa] = useState('');
  const [secret2fa, setSecret2fa] = useState('');
  const [code2fa, setCode2fa] = useState('');
  const [loading2fa, setLoading2fa] = useState(false);
  const [showDisable2fa, setShowDisable2fa] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling2fa, setDisabling2fa] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  
  // Pricing states
  const [pricingFileId, setPricingFileId] = useState(null);
  const [pricingAmount, setPricingAmount] = useState('');
  const [pricingCurrency, setPricingCurrency] = useState('EUR');
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Sales states
  const [salesData, setSalesData] = useState(null);
  const [loadingSales, setLoadingSales] = useState(false);

  // Stripe Connect states
  const [connectStatus, setConnectStatus] = useState(null);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [connectAction, setConnectAction] = useState(false);
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);

  // Background management states
  const [userBgs, setUserBgs] = useState([]);
  const [loadingBgs, setLoadingBgs] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [selectedBgFile, setSelectedBgFile] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [bgDuration, setBgDuration] = useState(10);
  const [bgLink, setBgLink] = useState('');
  const [randomOrder, setRandomOrder] = useState(false);
  const [savingRandomOrder, setSavingRandomOrder] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const filteredFiles = useMemo(() => {
    let result = [...files];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(f => 
        f.filename.toLowerCase().includes(query) ||
        f.original_filenames?.some(n => n.toLowerCase().includes(query))
      );
    }
    if (filterStatus === 'active') {
      result = result.filter(f => !f.is_expired && f.is_active);
    } else if (filterStatus === 'expired') {
      result = result.filter(f => f.is_expired || !f.is_active);
    }
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc': return new Date(a.upload_date) - new Date(b.upload_date);
        case 'downloads': return (b.download_count || 0) - (a.download_count || 0);
        case 'size': return b.file_size - a.file_size;
        default: return new Date(b.upload_date) - new Date(a.upload_date);
      }
    });
    return result;
  }, [files, searchQuery, filterStatus, sortBy]);

  const totalPages = Math.ceil(filteredFiles.length / ITEMS_PER_PAGE);
  const paginatedFiles = filteredFiles.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterStatus, sortBy]);

  useEffect(() => {
    if (!loading && !isAuthenticated) { navigate('/'); return; }
    if (isAuthenticated && token) loadUserFiles();
  }, [isAuthenticated, loading, navigate, token]);

  // Handle URL params for Stripe Connect return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'connect') {
      setActiveTab('connect');
      if (params.get('onboarding') === 'complete') {
        toast({ title: 'Onboarding terminé', description: 'Vérification du statut de votre compte...' });
      }
      if (token) loadConnectStatus();
      // Clean URL params
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [token]);

  useEffect(() => { if (user?.name) setName(user.name); }, [user]);

  const loadUserFiles = async () => {
    try {
      const res = await axios.get(`${API}/users/me/files`, { headers: { Authorization: `Bearer ${token}` } });
      setFiles(res.data.files || []);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadSalesData = async () => {
    setLoadingSales(true);
    try {
      const res = await axios.get(`${API}/users/me/sales`, { headers: { Authorization: `Bearer ${token}` } });
      setSalesData(res.data);
    } catch (error) {
      console.error('Error loading sales:', error);
    } finally {
      setLoadingSales(false);
    }
  };

  const loadConnectStatus = async () => {
    setLoadingConnect(true);
    try {
      const res = await axios.get(`${API}/stripe-connect/status`, { headers: { Authorization: `Bearer ${token}` } });
      setConnectStatus(res.data);
    } catch (error) {
      console.error('Error loading connect status:', error);
    } finally {
      setLoadingConnect(false);
    }
  };

  const handleSaveStripeKeys = async () => {
    if (!stripeSecretKey.trim()) {
      toast({ title: 'Erreur', description: 'La clé secrète Stripe est requise', variant: 'destructive' });
      return;
    }
    setConnectAction(true);
    try {
      const res = await axios.post(`${API}/stripe-connect/save-keys`, {
        secret_key: stripeSecretKey,
        publishable_key: stripePublishableKey
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Clés Stripe enregistrées', description: `Mode: ${res.data.mode}` });
      setStripeSecretKey('');
      setStripePublishableKey('');
      loadConnectStatus();
    } catch (error) {
      toast({ title: 'Erreur Stripe', description: error.response?.data?.detail || 'Impossible de sauvegarder les clés', variant: 'destructive' });
    } finally {
      setConnectAction(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm('Supprimer vos clés Stripe ? Vous ne pourrez plus recevoir de paiements.')) return;
    setConnectAction(true);
    try {
      await axios.delete(`${API}/stripe-connect/disconnect`, { headers: { Authorization: `Bearer ${token}` } });
      setConnectStatus({ connected: false, status: 'none' });
      toast({ title: 'Compte Stripe déconnecté' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setConnectAction(false);
    }
  };

  // ============ BACKGROUND MANAGEMENT ============
  const loadUserBackgrounds = async () => {
    if (!user?.id) return;
    setLoadingBgs(true);
    try {
      const res = await axios.get(`${API}/user/${user.id}/backgrounds`, { headers: { Authorization: `Bearer ${token}` } });
      setUserBgs(res.data.backgrounds || []);
      setRandomOrder(res.data.random_order || false);
    } catch (e) { console.error('Error loading backgrounds:', e); }
    setLoadingBgs(false);
  };

  const toggleRandomOrder = async () => {
    setSavingRandomOrder(true);
    try {
      const newValue = !randomOrder;
      await axios.put(`${API}/user/${user.id}/backgrounds-settings`, {
        random_order: newValue
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRandomOrder(newValue);
      toast({ title: newValue ? 'Ordre aléatoire activé' : 'Ordre séquentiel activé' });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de modifier le paramètre', variant: 'destructive' });
    }
    setSavingRandomOrder(false);
  };

  const handleBgFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedBgFile(file);
    setBgPreview(URL.createObjectURL(file));
  };

  const handleBgUpload = async () => {
    if (!selectedBgFile || !user?.id) return;
    setUploadingBg(true);
    try {
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(selectedBgFile.size / CHUNK_SIZE);
      const initRes = await axios.post(`${API}/user/${user.id}/background/initiate`, {
        filename: selectedBgFile.name, file_size: selectedBgFile.size,
        mime_type: selectedBgFile.type, total_chunks: totalChunks
      }, { headers: { Authorization: `Bearer ${token}` } });
      const uploadId = initRes.data.upload_id;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const chunk = selectedBgFile.slice(start, start + CHUNK_SIZE);
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(chunk);
        });
        await axios.post(`${API}/user/background/chunk`, { upload_id: uploadId, chunk_index: i, chunk_data: base64 }, { headers: { Authorization: `Bearer ${token}` } });
      }
      await axios.post(`${API}/user/background/complete`, {
        upload_id: uploadId, duration: bgDuration, link: bgLink
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Fond ajouté avec succès' });
      setSelectedBgFile(null); setBgPreview(null); setBgDuration(10); setBgLink('');
      loadUserBackgrounds();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur lors de l\'upload', variant: 'destructive' });
    }
    setUploadingBg(false);
  };

  const handleDeleteBg = async (bgId) => {
    if (!window.confirm('Supprimer ce fond ?')) return;
    try {
      await axios.delete(`${API}/user/${user.id}/backgrounds/${bgId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Fond supprimé' });
      loadUserBackgrounds();
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const updateUserBg = async (bgId, field, value) => {
    try {
      await axios.put(`${API}/user/${user.id}/backgrounds/${bgId}`, {
        [field]: value
      }, { headers: { Authorization: `Bearer ${token}` } });
      loadUserBackgrounds();
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const moveUserBg = async (bgId, direction) => {
    const idx = userBgs.findIndex(bg => bg.id === bgId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= userBgs.length) return;
    await updateUserBg(userBgs[idx].id, 'order', newIdx);
    await updateUserBg(userBgs[newIdx].id, 'order', idx);
    loadUserBackgrounds();
  };

  const handleSetPricing = async (shareId) => {
    if (!pricingAmount || parseFloat(pricingAmount) <= 0) {
      toast({ title: 'Erreur', description: 'Veuillez entrer un prix valide', variant: 'destructive' });
      return;
    }
    setSavingPrice(true);
    try {
      await axios.put(`${API}/users/me/files/${shareId}/pricing`, 
        { price: parseFloat(pricingAmount), currency: pricingCurrency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Prix défini', description: `${pricingAmount} ${pricingCurrency}` });
      setPricingFileId(null);
      setPricingAmount('');
      loadUserFiles();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Impossible de définir le prix', variant: 'destructive' });
    } finally {
      setSavingPrice(false);
    }
  };

  const handleRemovePricing = async (shareId) => {
    try {
      await axios.delete(`${API}/users/me/files/${shareId}/pricing`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Prix supprimé', description: 'Le fichier est à nouveau gratuit' });
      loadUserFiles();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const copyShareLink = (shareId) => {
    navigator.clipboard.writeText(`${window.location.origin}/share/${shareId}`);
    toast({ title: 'Lien copié', description: 'Le lien a été copié dans le presse-papiers' });
  };

  const deleteShare = async (shareId) => {
    if (!confirm('Supprimer ce partage ?')) return;
    try {
      await axios.delete(`${API}/share/${shareId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Supprimé' });
      loadUserFiles();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile({ name });
      toast({ title: 'Profil mis à jour' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleToggleNotifications = async () => {
    if (!isPro) {
      toast({ title: 'Fonctionnalité Pro', description: 'Passez à Pro pour activer les notifications', variant: 'destructive' });
      return;
    }
    setSavingNotif(true);
    try {
      await updateNotifications(!downloadNotifications);
      toast({ title: downloadNotifications ? 'Notifications désactivées' : 'Notifications activées' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally { setSavingNotif(false); }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast({ title: 'Erreur', description: 'Veuillez remplir tous les champs', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: 'Erreur', description: 'Le nouveau mot de passe doit contenir au moins 6 caractères', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Erreur', description: 'Les mots de passe ne correspondent pas', variant: 'destructive' });
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Succès', description: 'Mot de passe modifié avec succès' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || 'Impossible de modifier le mot de passe', 
        variant: 'destructive' 
      });
    } finally { setSavingPassword(false); }
  };

  const handle2faSetup = async () => {
    setLoading2fa(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setQrCode2fa(res.data.qr_code);
      setSecret2fa(res.data.secret);
      setShow2faSetup(true);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de configurer la 2FA', variant: 'destructive' });
    } finally { setLoading2fa(false); }
  };

  const handle2faVerify = async () => {
    setLoading2fa(true);
    try {
      await axios.post(`${API}/auth/2fa/verify-setup`, { code: code2fa }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Double authentification activée !' });
      setShow2faSetup(false);
      setCode2fa('');
      // Refresh user to get updated 2FA status
      window.location.reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Code invalide', variant: 'destructive' });
    } finally { setLoading2fa(false); }
  };

  const handle2faDisable = async () => {
    setDisabling2fa(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password: disablePassword }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: '2FA désactivée' });
      setShowDisable2fa(false);
      setDisablePassword('');
      window.location.reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de désactiver', variant: 'destructive' });
    } finally { setDisabling2fa(false); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 o';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024*1024) return `${(bytes/1024).toFixed(0)} Ko`;
    if (bytes < 1024*1024*1024) return `${(bytes/(1024*1024)).toFixed(1)} Mo`;
    return `${(bytes/(1024*1024*1024)).toFixed(2)} Go`;
  };

  const formatDate = (d) => {
    const dateStr = typeof d === 'string' && !d.endsWith('Z') && !d.includes('+') ? d + 'Z' : d;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const getDaysRemaining = (d) => {
    const dateStr = typeof d === 'string' && !d.endsWith('Z') && !d.includes('+') ? d + 'Z' : d;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000*60*60*24));
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
    </div>
  );

  if (!user) return null;

  const totalSize = files.reduce((acc, f) => acc + (f.file_size || 0), 0);
  const totalDownloads = files.reduce((acc, f) => acc + (f.download_count || 0), 0);
  const activeFiles = files.filter(f => !f.is_expired && f.is_active).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <Toaster />
      <NavigationMenu />
      
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header with profile */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-blue-400 hover:text-blue-300">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white" data-testid="dashboard-title">Tableau de bord</h1>
                  <p className="text-blue-300/70 text-sm flex items-center gap-2">
                    {user.email}
                    {isPro && <span className="text-yellow-400 flex items-center gap-1"><Crown className="w-3 h-3" /> Pro</span>}
                  </p>
                </div>
              </div>
            </div>
            <Button onClick={loadUserFiles} variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg"><FileArchive className="w-5 h-5 text-blue-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{files.length}</p>
                  <p className="text-xs text-blue-300/60">Partages</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-green-500/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg"><Download className="w-5 h-5 text-green-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{totalDownloads}</p>
                  <p className="text-xs text-green-300/60">Téléchargements</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg"><HardDrive className="w-5 h-5 text-cyan-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatSize(totalSize)}</p>
                  <p className="text-xs text-cyan-300/60">Stockage utilisé</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg"><BarChart3 className="w-5 h-5 text-purple-400" /></div>
                <div>
                  <p className="text-2xl font-bold text-white">{activeFiles}</p>
                  <p className="text-xs text-purple-300/60">Actifs</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-blue-500/10 pb-3">
            <button onClick={() => setActiveTab('files')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'files' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-blue-300/60 hover:text-blue-300'}`}>
              <FileArchive className="w-4 h-4 inline mr-2" /> Mes partages
            </button>
            {isPro && (
              <button onClick={() => { setActiveTab('sales'); loadSalesData(); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'sales' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'text-blue-300/60 hover:text-blue-300'}`}
                data-testid="sales-tab-btn">
                <DollarSign className="w-4 h-4 inline mr-2" /> Mes ventes
              </button>
            )}
            {isPro && (
              <button onClick={() => { setActiveTab('connect'); loadConnectStatus(); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'connect' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-blue-300/60 hover:text-blue-300'}`}
                data-testid="connect-tab-btn">
                <Link2 className="w-4 h-4 inline mr-2" /> Stripe Connect
              </button>
            )}
            <button onClick={() => setActiveTab('profile')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'profile' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-blue-300/60 hover:text-blue-300'}`}>
              <User className="w-4 h-4 inline mr-2" /> Mon profil
            </button>
            {isPro && (
              <>
              <button onClick={() => setActiveTab('extensions')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'extensions' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-blue-300/60 hover:text-blue-300'}`}
                data-testid="extensions-tab-btn">
                <Puzzle className="w-4 h-4 inline mr-2" /> Extensions
              </button>
              <button onClick={() => { setActiveTab('backgrounds'); loadUserBackgrounds(); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'backgrounds' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-blue-300/60 hover:text-blue-300'}`}
                data-testid="backgrounds-tab-btn">
                <Image className="w-4 h-4 inline mr-2" /> Fond personnalisé
              </button>
              </>
            )}
          </div>

          {/* FILES TAB */}
          {activeTab === 'files' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300/40" />
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un fichier..."
                    className="w-full bg-slate-800/50 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400" />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300/40 hover:text-blue-300">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-slate-800/50 border border-blue-500/20 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-400 cursor-pointer">
                  <option value="all">Tous</option>
                  <option value="active">Actifs</option>
                  <option value="expired">Expirés</option>
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                  className="bg-slate-800/50 border border-blue-500/20 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-400 cursor-pointer">
                  <option value="date_desc">Plus récents</option>
                  <option value="date_asc">Plus anciens</option>
                  <option value="downloads">Plus téléchargés</option>
                  <option value="size">Plus volumineux</option>
                </select>
              </div>

              {/* Files list */}
              {loadingFiles ? (
                <div className="text-center py-12"><Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto" /></div>
              ) : filteredFiles.length === 0 ? (
                <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-dashed border-blue-500/20">
                  <FileArchive className="w-10 h-10 text-blue-300/30 mx-auto mb-3" />
                  <p className="text-blue-300/50">{files.length === 0 ? 'Aucun partage pour le moment' : 'Aucun résultat'}</p>
                  {files.length === 0 && (
                    <Link to="/" className="text-blue-400 text-sm hover:text-blue-300 mt-2 inline-block">Envoyer votre premier fichier</Link>
                  )}
                </div>
              ) : (
                <>
                <div className="space-y-3">
                  {paginatedFiles.map((file) => {
                    const daysLeft = getDaysRemaining(file.expiry_date);
                    const isExpired = daysLeft <= 0 || !file.is_active;
                    return (
                      <div key={file.share_id} className={`bg-slate-800/50 border rounded-xl p-4 transition-all ${isExpired ? 'border-red-500/20 opacity-60' : 'border-blue-500/20 hover:border-blue-500/30'}`}>
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${isExpired ? 'bg-red-500/10' : file.is_paid ? 'bg-emerald-500/10' : 'bg-blue-500/10'}`}>
                            {file.is_paid ? (
                              <DollarSign className="w-6 h-6 text-emerald-400" />
                            ) : (
                              <FileArchive className={`w-6 h-6 ${isExpired ? 'text-red-400' : 'text-blue-400'}`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-white truncate">{file.filename}</h3>
                              {file.has_password && <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded-full">Protégé</span>}
                              {file.is_paid && (
                                <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`price-badge-${file.share_id}`}>
                                  <Tag className="w-3 h-3" /> {file.price} {file.currency}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-blue-300/50">
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(file.upload_date)}</span>
                              <span>{formatSize(file.file_size)}</span>
                              <span>{file.file_count || 1} fichier{(file.file_count || 1) > 1 ? 's' : ''}</span>
                              <span className="flex items-center gap-1"><Download className="w-3 h-3" />{file.download_count || 0}</span>
                              {isExpired ? (
                                <span className="text-red-400">Expiré</span>
                              ) : (
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{daysLeft}j restants</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!isExpired && isPro && (
                              <>
                                {file.is_paid ? (
                                  <button onClick={() => handleRemovePricing(file.share_id)}
                                    className="p-2 text-emerald-300/60 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors" 
                                    title="Retirer le prix"
                                    data-testid={`remove-price-btn-${file.share_id}`}>
                                    <X className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button onClick={() => { setPricingFileId(file.share_id); setPricingAmount(''); }}
                                    className="p-2 text-emerald-300/60 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors" 
                                    title="Vendre ce fichier"
                                    data-testid={`set-price-btn-${file.share_id}`}>
                                    <Tag className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )}
                            {!isExpired && (
                              <>
                                <button onClick={() => copyShareLink(file.share_id)}
                                  className="p-2 text-blue-300/60 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors" title="Copier le lien">
                                  <Copy className="w-4 h-4" />
                                </button>
                                <a href={`/share/${file.share_id}`} target="_blank" rel="noreferrer"
                                  className="p-2 text-blue-300/60 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors" title="Ouvrir">
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </>
                            )}
                            <button onClick={() => deleteShare(file.share_id)}
                              className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Supprimer">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Pricing inline form */}
                        {pricingFileId === file.share_id && (
                          <div className="mt-3 pt-3 border-t border-blue-500/10 flex items-center gap-3" data-testid={`pricing-form-${file.share_id}`}>
                            <Tag className="w-4 h-4 text-emerald-400 shrink-0" />
                            <input
                              type="number"
                              step="0.01"
                              min="0.50"
                              placeholder="Prix"
                              value={pricingAmount}
                              onChange={(e) => setPricingAmount(e.target.value)}
                              className="w-28 bg-slate-900/50 border border-emerald-500/30 rounded-lg px-3 py-1.5 text-sm text-white placeholder-blue-300/30 focus:outline-none focus:border-emerald-400"
                              data-testid={`pricing-input-${file.share_id}`}
                            />
                            <select value={pricingCurrency} onChange={(e) => setPricingCurrency(e.target.value)}
                              className="bg-slate-900/50 border border-emerald-500/30 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none">
                              <option value="EUR">EUR</option>
                              <option value="USD">USD</option>
                              <option value="GBP">GBP</option>
                            </select>
                            <Button size="sm" onClick={() => handleSetPricing(file.share_id)} disabled={savingPrice}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                              data-testid={`confirm-price-btn-${file.share_id}`}>
                              {savingPrice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <button onClick={() => setPricingFileId(null)} className="text-blue-300/40 hover:text-blue-300">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-blue-500/10" data-testid="pagination-controls">
                    <p className="text-sm text-blue-300/50">
                      {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredFiles.length)} sur {filteredFiles.length} partages
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 text-sm rounded-lg border border-blue-500/20 text-blue-300/70 hover:bg-blue-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid="pagination-prev">
                        Précédent
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, idx) =>
                          p === '...' ? (
                            <span key={`dots-${idx}`} className="px-2 text-blue-300/30">...</span>
                          ) : (
                            <button key={p} onClick={() => setCurrentPage(p)}
                              className={`w-9 h-9 text-sm rounded-lg transition-colors ${currentPage === p ? 'bg-blue-500 text-white font-semibold' : 'text-blue-300/70 hover:bg-blue-500/10 border border-blue-500/20'}`}
                              data-testid={`pagination-page-${p}`}>
                              {p}
                            </button>
                          )
                        )}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 text-sm rounded-lg border border-blue-500/20 text-blue-300/70 hover:bg-blue-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid="pagination-next">
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
          )}

          {/* SALES TAB */}
          {activeTab === 'sales' && isPro && (
            <div className="space-y-6">
              {loadingSales ? (
                <div className="text-center py-12"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" /></div>
              ) : salesData ? (
                <>
                  {/* Sales stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-800/50 border border-emerald-500/20 rounded-xl p-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/20 rounded-lg"><TrendingUp className="w-5 h-5 text-emerald-400" /></div>
                        <div>
                          <p className="text-2xl font-bold text-white" data-testid="total-revenue">{salesData.total_revenue?.toFixed(2) || '0.00'} EUR</p>
                          <p className="text-xs text-emerald-300/60">Revenus totaux</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 border border-emerald-500/20 rounded-xl p-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/20 rounded-lg"><ShoppingCart className="w-5 h-5 text-emerald-400" /></div>
                        <div>
                          <p className="text-2xl font-bold text-white" data-testid="total-sales">{salesData.total_sales || 0}</p>
                          <p className="text-xs text-emerald-300/60">Ventes totales</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 border border-emerald-500/20 rounded-xl p-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/20 rounded-lg"><Tag className="w-5 h-5 text-emerald-400" /></div>
                        <div>
                          <p className="text-2xl font-bold text-white">{salesData.paid_files?.length || 0}</p>
                          <p className="text-xs text-emerald-300/60">Fichiers en vente</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Paid files list */}
                  {salesData.paid_files?.length > 0 && (
                    <div className="bg-slate-800/50 border border-emerald-500/20 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Tag className="w-5 h-5 text-emerald-400" /> Fichiers en vente
                      </h3>
                      <div className="space-y-3">
                        {salesData.paid_files.map((f) => (
                          <div key={f.share_id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-emerald-500/10">
                            <div className="flex items-center gap-3">
                              <DollarSign className="w-5 h-5 text-emerald-400" />
                              <div>
                                <p className="text-white text-sm font-medium truncate max-w-[300px]">{f.filename}</p>
                                <p className="text-xs text-emerald-300/50">{f.total_sales} vente{f.total_sales !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-emerald-300 font-bold">{f.price} {f.currency}</p>
                              <p className="text-xs text-emerald-300/50">Revenu: {f.total_revenue?.toFixed(2)} {f.currency}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sales history */}
                  <div className="bg-slate-800/50 border border-emerald-500/20 rounded-xl p-5">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-emerald-400" /> Historique des ventes
                    </h3>
                    {salesData.sales?.length === 0 ? (
                      <div className="text-center py-8">
                        <ShoppingCart className="w-10 h-10 text-emerald-300/20 mx-auto mb-3" />
                        <p className="text-blue-300/50">Aucune vente pour le moment</p>
                        <p className="text-xs text-blue-300/30 mt-1">Définissez un prix sur vos fichiers dans l'onglet "Mes partages"</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-blue-300/50 border-b border-blue-500/10">
                              <th className="text-left py-2 px-3">Fichier</th>
                              <th className="text-left py-2 px-3">Acheteur</th>
                              <th className="text-right py-2 px-3">Montant</th>
                              <th className="text-right py-2 px-3">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesData.sales.map((sale) => (
                              <tr key={sale.purchase_id} className="border-b border-blue-500/5 hover:bg-slate-900/30">
                                <td className="py-2.5 px-3 text-white truncate max-w-[200px]">{sale.filename}</td>
                                <td className="py-2.5 px-3 text-blue-300/70">{sale.buyer_email || 'Anonyme'}</td>
                                <td className="py-2.5 px-3 text-emerald-300 font-medium text-right">{sale.amount?.toFixed(2)} {sale.currency}</td>
                                <td className="py-2.5 px-3 text-blue-300/50 text-right">{sale.paid_at ? formatDate(sale.paid_at) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-blue-300/50">Impossible de charger les données de vente</p>
                </div>
              )}
            </div>
          )}


          {/* STRIPE CONNECT TAB */}
          {activeTab === 'connect' && isPro && (
            <div className="max-w-2xl space-y-6">
              {loadingConnect ? (
                <div className="text-center py-12"><Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto" /></div>
              ) : !connectStatus || !connectStatus.connected ? (
                /* Not connected - show key input form */
                <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6 space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-500/20 rounded-xl">
                      <Link2 className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Configurer Stripe</h3>
                      <p className="text-sm text-blue-300/60 mt-1">
                        Entrez vos clés API Stripe pour recevoir les paiements de vos fichiers payants directement sur votre compte.
                      </p>
                    </div>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-sm text-blue-200">Paiements versés directement sur votre compte Stripe</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-sm text-blue-200">Aucune commission SENDBOX - vous gardez 100% (hors frais Stripe)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-sm text-blue-200">Gérez vos ventes depuis votre propre dashboard Stripe</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-blue-300/70 mb-1.5">Clé secrète Stripe *</label>
                      <div className="relative">
                        <input
                          type={showSecretKey ? 'text' : 'password'}
                          value={stripeSecretKey}
                          onChange={e => setStripeSecretKey(e.target.value)}
                          placeholder="sk_live_... ou sk_test_..."
                          className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-teal-400 pr-10 font-mono text-sm"
                          data-testid="stripe-secret-key-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecretKey(!showSecretKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300/40 hover:text-blue-300"
                        >
                          {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-blue-300/30 mt-1">
                        Trouvez-la sur <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-teal-400/60 hover:text-teal-400 underline">dashboard.stripe.com/apikeys</a>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm text-blue-300/70 mb-1.5">Clé publique Stripe (optionnel)</label>
                      <input
                        value={stripePublishableKey}
                        onChange={e => setStripePublishableKey(e.target.value)}
                        placeholder="pk_live_... ou pk_test_..."
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-teal-400 font-mono text-sm"
                        data-testid="stripe-publishable-key-input"
                      />
                    </div>
                  </div>
                  <Button onClick={handleSaveStripeKeys} disabled={connectAction || !stripeSecretKey.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                    data-testid="connect-stripe-btn">
                    {connectAction ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer mes clés Stripe
                  </Button>
                </div>
              ) : connectStatus.status === 'active' ? (
                /* Active - keys saved */
                <div className="space-y-4">
                  <div className="bg-slate-800/50 border border-emerald-500/20 rounded-xl p-6">
                    <div className="flex items-start gap-4 mb-5">
                      <div className="p-3 bg-emerald-500/20 rounded-xl">
                        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Stripe configuré</h3>
                        <p className="text-sm text-emerald-300/60 mt-1">
                          Votre compte Stripe est connecté. Les paiements de vos fichiers payants sont versés directement sur votre compte.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-5">
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <p className="text-xs text-blue-300/50 mb-1">Clé secrète</p>
                        <p className="text-sm text-white font-mono">{connectStatus.masked_secret_key}</p>
                      </div>
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <p className="text-xs text-blue-300/50 mb-1">Mode</p>
                        <p className={`text-sm font-medium ${connectStatus.mode === 'live' ? 'text-emerald-400' : 'text-yellow-400'}`}>
                          {connectStatus.mode === 'live' ? 'Production' : 'Test'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer"
                        className="flex-1">
                        <Button className="w-full bg-slate-700 hover:bg-slate-600 text-white"
                          data-testid="stripe-dashboard-btn">
                          <ExternalLink className="w-4 h-4 mr-2" /> Dashboard Stripe
                        </Button>
                      </a>
                      <Button onClick={handleDisconnectStripe} variant="outline" disabled={connectAction}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid="disconnect-stripe-active-btn">
                        {connectAction ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* EXTENSIONS TAB */}
          {activeTab === 'extensions' && isPro && (
            <div className="max-w-2xl space-y-6" data-testid="extensions-tab-content">
              <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Puzzle className="w-5 h-5 text-blue-400" /> Extensions email
                </h3>
                <p className="text-blue-300/60 text-sm mb-6">Envoyez des fichiers volumineux directement depuis votre client email favori.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link to="/outlook" className="bg-slate-900/50 border border-blue-500/15 rounded-xl p-5 hover:border-blue-500/30 transition-all group" data-testid="ext-outlook-card">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-500/15 rounded-lg group-hover:bg-blue-500/25 transition-colors">
                        <svg viewBox="0 0 24 24" className="w-6 h-6 text-blue-400" fill="currentColor"><path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.33.75.1.43.1.9zm-1.98-.01q0-.34-.06-.62-.06-.28-.18-.48-.12-.2-.31-.31-.2-.12-.48-.12-.29 0-.49.13t-.34.33q-.14.2-.2.47-.06.26-.06.57 0 .3.06.57.06.27.2.47.14.2.34.32.2.13.49.13.28 0 .47-.12.2-.12.31-.31.12-.2.18-.47.07-.28.07-.57zm6.6 2.06H11V9.38h2.13c.35 0 .66.05.93.16.27.1.5.26.67.45.17.2.3.43.38.7.08.27.13.57.13.9 0 .32-.05.62-.14.9-.1.27-.23.5-.4.7-.18.2-.4.35-.67.46-.26.1-.57.16-.93.16zM12.15 13h.76c.19 0 .37-.03.54-.08.17-.06.31-.14.43-.26.12-.11.22-.26.29-.44.07-.18.1-.4.1-.66 0-.24-.03-.45-.1-.63-.06-.17-.16-.31-.28-.42-.12-.11-.26-.19-.43-.24-.17-.05-.35-.08-.53-.08h-.78v2.8zM2 3.993A1 1 0 013 3h18a1 1 0 011 1v16a1 1 0 01-1 1H3a1 1 0 01-1-1V3.993zM3 19h18V5H3v14z"/></svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white text-sm">Outlook</h4>
                        <p className="text-xs text-blue-300/50">Desktop, Web et Mobile</p>
                      </div>
                    </div>
                    <p className="text-xs text-blue-300/60">Add-in Microsoft Office pour envoyer des fichiers depuis Outlook.</p>
                  </Link>
                  <Link to="/gmail-extension" className="bg-slate-900/50 border border-blue-500/15 rounded-xl p-5 hover:border-blue-500/30 transition-all group" data-testid="ext-gmail-card">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-red-500/15 rounded-lg group-hover:bg-red-500/25 transition-colors">
                        <svg viewBox="0 0 24 24" className="w-6 h-6 text-red-400" fill="currentColor"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/></svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white text-sm">Gmail</h4>
                        <p className="text-xs text-blue-300/50">Extension Chrome</p>
                      </div>
                    </div>
                    <p className="text-xs text-blue-300/60">Extension Chrome pour envoyer des fichiers depuis Gmail.</p>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* BACKGROUNDS TAB */}
          {activeTab === 'backgrounds' && isPro && (
            <div className="space-y-6" data-testid="backgrounds-tab-content">
              {/* Existing backgrounds list */}
              {loadingBgs ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>
              ) : userBgs.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-blue-400" /> Séquence des fonds ({userBgs.length})
                    </h2>
                    <button
                      onClick={toggleRandomOrder}
                      disabled={savingRandomOrder}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        randomOrder
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                          : 'bg-slate-700/50 text-blue-300/50 border border-blue-500/10 hover:border-blue-500/30'
                      }`}
                      data-testid="random-order-toggle"
                    >
                      {savingRandomOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
                      Ordre aléatoire
                    </button>
                  </div>
                  <p className="text-blue-300/50 text-sm">
                    {randomOrder
                      ? 'Les fonds seront mélangés aléatoirement à chaque visite.'
                      : 'Les fonds défilent automatiquement sur vos pages de partage.'}
                  </p>
                  {/* Stats summary */}
                  <div className="flex items-center gap-4 bg-slate-800/30 border border-blue-500/10 rounded-xl px-4 py-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-sm text-white font-medium" data-testid="bg-views-total">{userBgs.reduce((sum, bg) => sum + (bg.views || 0), 0)} affichages</p>
                        <p className="text-xs text-blue-300/50">sur {userBgs.length} fond(s)</p>
                      </div>
                    </div>
                    {userBgs.some(bg => (bg.clicks || 0) > 0) && (
                      <div className="flex items-center gap-2 border-l border-blue-500/20 pl-4">
                        <MousePointerClick className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-sm text-white font-medium" data-testid="bg-clicks-total">{userBgs.reduce((sum, bg) => sum + (bg.clicks || 0), 0)} clics</p>
                          <p className="text-xs text-blue-300/50">sur {userBgs.filter(bg => bg.link).length} fond(s) avec lien</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Click Stats Chart */}
                  <BackgroundClicksChart userId={user?.id} token={token} backgrounds={userBgs} />
                  {/* Cross-fade Preview */}
                  {userBgs.length >= 2 && (
                    <BackgroundPreviewSlideshow backgrounds={userBgs} />
                  )}
                  {userBgs.map((bg, i) => (
                    <div key={bg.id || i} className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4 flex gap-4" data-testid={`user-bg-item-${i}`}>
                      <div className="w-40 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-900 relative">
                        {bg.type === 'video' ? (
                          <video src={bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`} className="w-full h-full object-cover" controls playsInline />
                        ) : (
                          <img src={bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`} className="w-full h-full object-cover" alt={`Fond ${i + 1}`} />
                        )}
                        <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                          {bg.type === 'video' ? <Video className="w-3 h-3 inline mr-1" /> : <Image className="w-3 h-3 inline mr-1" />}
                          #{i + 1}
                        </div>
                        <div className="absolute bottom-1 right-1 flex gap-1">
                          <div className="bg-black/60 text-cyan-300 text-xs px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`bg-views-${i}`}>
                            <Eye className="w-3 h-3" />
                            {bg.views || 0}
                          </div>
                          {bg.link && (
                            <div className="bg-black/60 text-blue-300 text-xs px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`bg-clicks-${i}`}>
                              <MousePointerClick className="w-3 h-3" />
                              {bg.clicks || 0}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <label className="text-xs text-blue-300/60 flex items-center gap-1"><Clock className="w-3 h-3" /> Durée (sec)</label>
                            <input type="number" min="3" max="120" value={bg.duration || 10}
                              onChange={(e) => updateUserBg(bg.id, 'duration', parseInt(e.target.value) || 10)}
                              className="w-24 bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-blue-400" />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-blue-300/60 flex items-center gap-1"><Link2 className="w-3 h-3" /> Lien cliquable</label>
                            <BgLinkInput bgId={bg.id} initialLink={bg.link} onSave={updateUserBg} />
                          </div>
                        </div>
                        <p className="text-xs text-blue-300/40 truncate">{bg.filename || bg.url?.split('/').pop()}</p>
                      </div>
                      <div className="flex flex-col gap-1 justify-center">
                        <button onClick={() => moveUserBg(bg.id, 'up')} disabled={i === 0}
                          className={`p-1.5 rounded-lg transition-colors ${i === 0 ? 'text-blue-300/20' : 'text-blue-300/60 hover:bg-blue-500/20 hover:text-blue-300'}`}>
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveUserBg(bg.id, 'down')} disabled={i === userBgs.length - 1}
                          className={`p-1.5 rounded-lg transition-colors ${i === userBgs.length - 1 ? 'text-blue-300/20' : 'text-blue-300/60 hover:bg-blue-500/20 hover:text-blue-300'}`}>
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteBg(bg.id)}
                          className="p-1.5 rounded-lg text-red-400/60 hover:bg-red-500/20 hover:text-red-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-800/30 border border-dashed border-blue-500/20 rounded-xl p-8 text-center">
                  <Monitor className="w-10 h-10 text-blue-300/30 mx-auto mb-3" />
                  <p className="text-blue-300/50 text-sm">Aucun fond personnalisé configuré</p>
                  <p className="text-blue-300/30 text-xs mt-1">Ajoutez des images ou vidéos pour personnaliser vos pages de partage</p>
                </div>
              )}

              {/* Upload new background */}
              {userBgs.length < 10 && (
                <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-blue-400" /> Ajouter un fond
                  </h2>

                  {bgPreview && (
                    <div className="mb-4 rounded-xl overflow-hidden border border-blue-500/20 relative max-h-48">
                      <button onClick={() => { setSelectedBgFile(null); setBgPreview(null); }}
                        className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full text-white hover:bg-red-600 z-10">
                        <X className="w-4 h-4" />
                      </button>
                      {selectedBgFile?.type?.startsWith('video/') ? (
                        <video src={bgPreview} className="w-full max-h-48 object-cover" controls playsInline />
                      ) : (
                        <img src={bgPreview} className="w-full max-h-48 object-cover" alt="Aperçu" />
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="text-sm text-blue-300/70 mb-1 block">Durée (secondes)</label>
                      <input type="number" min="3" max="120" value={bgDuration}
                        onChange={(e) => setBgDuration(parseInt(e.target.value) || 10)}
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-blue-300/70 mb-1 block">Lien cliquable (optionnel)</label>
                      <input type="url" value={bgLink}
                        onChange={(e) => setBgLink(e.target.value)}
                        placeholder="https://monsite.fr"
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 text-sm focus:outline-none focus:border-blue-400" />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <input id="bg-file-input" type="file" accept="image/*,video/*" className="hidden" onChange={handleBgFileSelect} />
                    <Button onClick={() => document.getElementById('bg-file-input').click()}
                      variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10">
                      <Image className="w-4 h-4 mr-2" /> Choisir un fichier
                    </Button>
                    {selectedBgFile && (
                      <Button onClick={handleBgUpload} disabled={uploadingBg}
                        className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="upload-bg-btn">
                        {uploadingBg ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload en cours...</> : <><Upload className="w-4 h-4 mr-2" /> Ajouter le fond</>}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-4 text-sm text-blue-300/50">
                {randomOrder ? (
                  <p>Les fonds seront affichés dans un ordre aléatoire à chaque visite.</p>
                ) : (
                  <p>Les fonds défilent automatiquement sur vos pages de partage selon la durée définie pour chacun.</p>
                )}
                <p className="mt-1">Si un lien est défini, le visiteur peut cliquer sur le fond pour l'ouvrir.</p>
                <p className="mt-1">Formats acceptés : images (JPG, PNG, WebP) et vidéos (MP4, WebM) jusqu'à 200 Mo.</p>
              </div>
            </div>
          )}

          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="max-w-2xl space-y-6">
              {/* Profile form */}
              <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6 space-y-4">
                <h3 className="text-lg font-semibold text-white">Informations personnelles</h3>
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Nom</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom"
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
                    <input type="email" value={user.email} disabled
                      className="w-full bg-slate-900/30 border border-blue-500/10 rounded-lg pl-10 pr-4 py-2.5 text-white/50 cursor-not-allowed" />
                  </div>
                </div>
                <Button onClick={handleSaveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>

              {/* Change Password */}
              <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6 space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Lock className="w-5 h-5 text-blue-400" /> Changer le mot de passe
                </h3>
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Mot de passe actuel</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
                    <input 
                      type={showCurrentPassword ? "text" : "password"} 
                      value={currentPassword} 
                      onChange={(e) => setCurrentPassword(e.target.value)} 
                      placeholder="••••••••"
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg pl-10 pr-12 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400/50 hover:text-blue-300"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Nouveau mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
                    <input 
                      type={showNewPassword ? "text" : "password"} 
                      value={newPassword} 
                      onChange={(e) => setNewPassword(e.target.value)} 
                      placeholder="••••••••"
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg pl-10 pr-12 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400/50 hover:text-blue-300"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-blue-300/50 mt-1">Minimum 6 caractères</p>
                </div>
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Confirmer le nouveau mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
                    <input 
                      type={showNewPassword ? "text" : "password"} 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)} 
                      placeholder="••••••••"
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400" 
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleChangePassword} 
                  disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword} 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Modifier le mot de passe
                </Button>
              </div>


              {/* Double authentification (2FA) */}
              {(isPro || isAdmin) && (
                <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6 space-y-4" data-testid="2fa-section">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-emerald-400" /> Double authentification (2FA)
                  </h3>

                  {user.two_factor_enabled ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-emerald-500/20">
                            <ShieldCheck className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white text-sm">2FA activée</h4>
                            <p className="text-xs text-emerald-400/70">Votre compte est protégé par la double authentification</p>
                          </div>
                        </div>
                        {!isAdmin && (
                          <Button
                            onClick={() => setShowDisable2fa(!showDisable2fa)}
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid="2fa-disable-toggle"
                          >
                            Désactiver
                          </Button>
                        )}
                        {isAdmin && (
                          <span className="text-xs text-blue-300/50 bg-blue-500/10 px-3 py-1 rounded-full">Obligatoire (admin)</span>
                        )}
                      </div>
                      {showDisable2fa && !isAdmin && (
                        <div className="bg-slate-900/50 border border-red-500/20 rounded-xl p-4 space-y-3">
                          <p className="text-sm text-red-300/70">Confirmez votre mot de passe pour désactiver la 2FA :</p>
                          <input
                            type="password"
                            value={disablePassword}
                            onChange={(e) => setDisablePassword(e.target.value)}
                            placeholder="Votre mot de passe"
                            className="w-full bg-slate-800/50 border border-red-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-red-400"
                            data-testid="2fa-disable-password"
                          />
                          <Button onClick={handle2faDisable} disabled={disabling2fa || !disablePassword}
                            className="bg-red-600 hover:bg-red-700 text-white" data-testid="2fa-disable-confirm-btn">
                            {disabling2fa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Confirmer la désactivation
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-slate-700/50">
                            <Shield className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white text-sm">2FA non activée</h4>
                            <p className="text-xs text-blue-300/50">Protégez votre compte avec un code temporaire</p>
                          </div>
                        </div>
                        <Button onClick={handle2faSetup} disabled={loading2fa}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="2fa-enable-btn">
                          {loading2fa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                          Activer
                        </Button>
                      </div>

                      {show2faSetup && (
                        <div className="bg-slate-900/50 border border-emerald-500/20 rounded-xl p-5 space-y-4">
                          <p className="text-sm text-blue-300/70">
                            Scannez le QR code avec votre application d'authentification (Google Authenticator, Authy, etc.) :
                          </p>
                          {qrCode2fa && (
                            <div className="flex justify-center">
                              <div className="bg-white p-3 rounded-xl">
                                <img src={qrCode2fa} alt="QR Code 2FA" className="w-48 h-48" data-testid="2fa-profile-qr" />
                              </div>
                            </div>
                          )}
                          <div className="bg-slate-800/50 rounded-lg p-3">
                            <p className="text-xs text-blue-300/50 mb-1">Clé secrète (saisie manuelle) :</p>
                            <code className="text-sm text-emerald-400 font-mono break-all">{secret2fa}</code>
                          </div>
                          <div>
                            <label className="block text-sm text-blue-300 mb-2">Code de vérification</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={code2fa}
                              onChange={(e) => setCode2fa(e.target.value.replace(/\D/g, ''))}
                              placeholder="000000"
                              className="w-full bg-slate-800/50 border border-blue-500/30 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-blue-300/30 focus:outline-none focus:border-emerald-400"
                              data-testid="2fa-profile-code-input"
                            />
                          </div>
                          <Button onClick={handle2faVerify} disabled={loading2fa || code2fa.length !== 6}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="2fa-profile-verify-btn">
                            {loading2fa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                            Activer la 2FA
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Notifications */}
              <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-400" /> Notifications
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${downloadNotifications && isPro ? 'bg-green-500/20' : 'bg-slate-700/50'}`}>
                      {downloadNotifications && isPro ? <Bell className="w-5 h-5 text-green-400" /> : <BellOff className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div>
                      <h4 className="font-medium text-white text-sm">Notifications de téléchargement</h4>
                      <p className="text-xs text-blue-300/50">Email à chaque téléchargement de vos fichiers</p>
                    </div>
                  </div>
                  <Button onClick={handleToggleNotifications} disabled={savingNotif}
                    variant={downloadNotifications && isPro ? "default" : "outline"}
                    className={downloadNotifications && isPro ? "bg-green-600 hover:bg-green-700 text-white" : "border-blue-500/30 text-blue-300"}>
                    {savingNotif ? <Loader2 className="w-4 h-4 animate-spin" /> : (downloadNotifications && isPro ? 'Activé' : 'Activer')}
                  </Button>
                </div>
                {!isPro && <p className="text-xs text-yellow-400/70 mt-3 flex items-center gap-1"><Crown className="w-3 h-3" /> Fonctionnalité réservée aux membres Pro</p>}
              </div>

              {/* Quick links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isPro && (
                  <Link to="/pricing"
                    className="bg-slate-800/50 border border-yellow-500/20 rounded-xl p-5 hover:bg-slate-800/70 hover:border-yellow-400/30 transition-all group">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-500/20 rounded-lg group-hover:bg-yellow-500/30"><Crown className="w-5 h-5 text-yellow-400" /></div>
                      <div>
                        <h3 className="font-medium text-white text-sm">Passer Pro</h3>
                        <p className="text-xs text-blue-300/60">Débloquer les fonctionnalités avancées</p>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <DynamicFooter />
    </div>
  );
};

export default DashboardPage;
